/* The post composer.
 *
 * A collapsed prompt on the feed which opens a MODAL, the way Facebook does
 * it. The modal matters for more than looks: the composer grows with photos
 * and a long body, and expanding that inline pushes the feed around underneath
 * someone who is trying to write.
 *
 * THREE WAYS IN beside the prompt: Go live, Meet, and the photo icon, which
 * opens the post itself where Photo, Video and Feeling are each named. A
 * live and a meeting are not posts, so each has its own way in — shown only
 * to whoever may arrange one (see ArrangeDialog).
 */
import { useEffect, useRef, useState } from 'react'
import { Clapperboard, Globe, Image as ImageIcon, Smile, Users, UsersRound, Video } from 'lucide-react'
import {
  ArrangeDialog,
  useArrangeable,
  type ArrangeMode,
} from '@/features/church/ArrangeDialog'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Modal } from '@/components/ui/modal'
import { useCan, useCreatePost, useFeelings, useMe } from '@/api/queries'
import { cn } from '@/lib/utils'
import {
  MAX_ATTACHMENTS,
  PhotoButton,
  PhotoGrid,
  usePhotoAttachments,
} from './PhotoPicker'
import type { Visibility } from '@/types/api'

const MAX = 5000

export function Composer({
  group,
}: {
  /* Set on a group's wall. The composer then posts THERE — no audience
     choice, because the wall you are standing on is the audience. */
  group?: { id: string; name: string }
} = {}) {
  const [open, setOpen] = useState<null | { files?: File[]; feeling?: boolean }>(null)
  const [arranging, setArranging] = useState<ArrangeMode | null>(null)
  const { data: me } = useMe()
  const can = useCan(me?.church_id ?? null, group?.id ?? null)
  const arrangeable = useArrangeable()

  if (!me) return null

  /* ONLY WHOEVER MAY BROADCAST SEES LIVE VIDEO. Anybody else is not
     offered the button rather than offered it and refused. */
  const mayGoLive =
    Boolean(me.church_id) && can('broadcast.create') && arrangeable.live
  /* Meet: for whoever may arrange a call, where the church offers one. */
  const mayMeet =
    Boolean(me.church_id) &&
    (can('calls.audio.create') || can('calls.video.create')) &&
    arrangeable.meet

  return (
    <>
      <Card>
        <div className="flex items-center gap-s2 p-s3">
          <Avatar name={me.first_name} src={me.avatar_url} />
          <button
            onClick={() => setOpen({})}
            className="h-10 min-w-0 flex-1 truncate rounded-pill bg-surface-2 px-s4 text-left text-md text-ink-2 transition-colors hover:bg-hover-2"
          >
            {group ? `Post to ${group.name}…` : 'Write your testimony and inspire others…'}
          </button>

          {mayGoLive ? (
            <IconAction label="Go live" onClick={() => setArranging('live')}>
              <Video className="h-[22px] w-[22px] text-danger" aria-hidden />
            </IconAction>
          ) : null}
          {mayMeet ? (
            <IconAction label="Meet" onClick={() => setArranging('meet')}>
              <UsersRound className="h-[22px] w-[22px] text-brand" aria-hidden />
            </IconAction>
          ) : null}
          <IconAction label="Photo, video or feeling" onClick={() => setOpen({})}>
            <ImageIcon className="h-[22px] w-[22px] text-success" aria-hidden />
          </IconAction>
        </div>
      </Card>

      {/* Mounted only while open, so the draft and its uploads are discarded
          on close rather than lingering behind the feed. */}
      {open ? (
        <ComposerModal
          group={group}
          initialFiles={open.files}
          startWithFeeling={open.feeling}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {arranging && me.church_id ? (
        <ArrangeDialog
          mode={arranging}
          churchId={me.church_id}
          group={group}
          onClose={() => setArranging(null)}
        />
      ) : null}
    </>
  )
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="grid h-10 w-10 shrink-0 place-items-center rounded-pill transition-colors hover:bg-hover"
    >
      {children}
    </button>
  )
}

export function ComposerModal({
  group,
  testimonyOf,
  initialFiles,
  startWithFeeling = false,
  onClose,
}: {
  group?: { id: string; name: string }
  /** Picked from the feed's Photo/video before the composer opened. */
  initialFiles?: File[]
  /** Opened from the feed's Feeling: the list is showing already. */
  startWithFeeling?: boolean
  /** Share My Testimony: the answered Prayer Request this post tells the
      story of. Set by the button on the request's own card — the post goes
      up as post_type 'testimony', linked to the request, and everyone who
      prayed on it is told. */
  testimonyOf?: string
  onClose: () => void
}) {
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<Visibility>(
    group ? 'group' : 'global',
  )
  const textarea = useRef<HTMLTextAreaElement>(null)

  const { data: me } = useMe()
  const createPost = useCreatePost()
  const photos = usePhotoAttachments()
  const feelings = useFeelings()
  const [feeling, setFeeling] = useState<string | null>(null)
  const [pickingFeeling, setPickingFeeling] = useState(startWithFeeling)
  const hasVideo = photos.items.some((i) => i.kind === 'video')

  // Files picked on the feed start uploading the moment the composer opens.
  const started = useRef(false)
  useEffect(() => {
    if (started.current || !initialFiles?.length) return
    started.current = true
    photos.add(initialFiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const chosen = feelings.data?.find((f) => f.kind === feeling) ?? null

  const canPostToChurch = Boolean(me?.church_id)
  const hasContent = Boolean(body.trim()) || photos.hasAny

  // Grow with the text rather than scrolling inside a fixed box.
  useEffect(() => {
    const el = textarea.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`
  }, [body])

  if (!me) return null

  function submit() {
    // Publishing mid-upload would silently drop the photo still in flight.
    if (!hasContent || createPost.isPending || photos.isUploading) return
    createPost.mutate(
      {
        body: body.trim(),
        post_type: testimonyOf ? 'testimony' : 'post',
        prayer_request_id: testimonyOf ?? null,
        visibility,
        church_id: visibility === 'church' ? me!.church_id : null,
        group_id: group?.id ?? null,
        media_ids: photos.mediaIds,
        feeling,
      },
      {
        onSuccess: () => {
          photos.reset()
          setFeeling(null)
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) {
          photos.reset()
          onClose()
        }
      }}
      title={testimonyOf ? 'Share My Testimony' : 'Create post'}
      // A stray click outside must not discard what someone has written.
      guardClose={hasContent}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!hasContent || photos.isUploading}
          loading={createPost.isPending}
        >
          {photos.videoProgress !== null
            ? `Uploading video ${Math.round(photos.videoProgress * 100)}%`
            : photos.isUploading
              ? 'Uploading…'
              : 'Post'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <div className="flex items-center gap-s3">
          <Avatar name={me.first_name} src={me.avatar_url} />
          <div>
            <div className="text-md">
              <span className="font-semibold">
                {me.first_name} {me.last_name ?? ''}
              </span>
              {chosen ? (
                <span className="text-ink-2">
                  {' '}is feeling {chosen.emoji} {chosen.label}
                </span>
              ) : null}
            </div>

            {/* Who sees it. On a group's wall there is no choice to make —
                the wall IS the audience — so the group is stated instead of
                offering options that would post somewhere else. */}
            {group ? (
              <div className="mt-0.5 flex items-center gap-1 text-sm text-ink-2">
                <Users className="h-3 w-3" aria-hidden />
                {group.name}
              </div>
            ) : canPostToChurch ? (
              <div className="mt-1 flex gap-1">
                <VisibilityChip
                  active={visibility === 'global'}
                  onClick={() => setVisibility('global')}
                  icon={<Globe className="h-3 w-3" />}
                  label="Everyone"
                />
                <VisibilityChip
                  active={visibility === 'church'}
                  onClick={() => setVisibility('church')}
                  icon={<Users className="h-3 w-3" />}
                  label="My church"
                />
              </div>
            ) : (
              <div className="mt-0.5 flex items-center gap-1 text-sm text-ink-2">
                <Globe className="h-3 w-3" aria-hidden />
                Everyone
              </div>
            )}
          </div>
        </div>

        <textarea
          ref={textarea}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX))}
          placeholder={
            testimonyOf
              ? 'Tell everyone how your prayer was answered…'
              : 'Write your testimony and inspire others…'
          }
          aria-label="Post text"
          rows={3}
          autoFocus={!startWithFeeling && !initialFiles?.length}
          className="w-full resize-none border-none bg-transparent text-lg outline-none placeholder:text-ink-2"
        />

        {pickingFeeling ? (
          <div className="rounded-md border border-line-2 p-s3">
            <div className="mb-s2 flex items-center justify-between">
              <span className="text-sm font-bold text-ink-2">
                How are you feeling?
              </span>
              {chosen ? (
                <button
                  type="button"
                  onClick={() => {
                    setFeeling(null)
                    setPickingFeeling(false)
                  }}
                  className="text-sm font-semibold text-brand hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-s2">
              {feelings.data?.map((f) => (
                <button
                  key={f.kind}
                  type="button"
                  onClick={() => {
                    // Tapping the chosen one again clears it, so there is no
                    // way to get stuck with a feeling you cannot remove.
                    setFeeling(feeling === f.kind ? null : f.kind)
                    setPickingFeeling(false)
                  }}
                  className={cn(
                    'rounded-pill border px-s3 py-1 text-base transition-colors',
                    feeling === f.kind
                      ? 'border-warning bg-warning-soft font-semibold text-warning-ink'
                      : 'border-line-2 hover:bg-hover',
                  )}
                >
                  <span aria-hidden className="mr-1">{f.emoji}</span>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <PhotoGrid items={photos.items} onRemove={photos.remove} />

        {/* ADD TO YOUR POST — each kind named, the way Facebook lays it
            out: a picture icon alone did not say it takes a video. A post
            is photos OR one video, so each is closed once the other is in. */}
        <div className="rounded-md border border-line-2 px-s2 py-1.5">
          <p className="px-s1 pb-1 text-sm font-semibold text-ink-2">Add to your post</p>
          <div className="flex flex-wrap items-center gap-1">
            <PhotoButton
              onPick={photos.add}
              accept="image/*"
              disabled={photos.isFull || hasVideo}
              label={
                hasVideo
                  ? 'One video per post'
                  : photos.isFull
                    ? `Up to ${MAX_ATTACHMENTS} photos`
                    : 'Photo'
              }
            >
              <ImageIcon className="h-5 w-5 text-success" aria-hidden /> Photo
            </PhotoButton>
            <PhotoButton
              onPick={photos.add}
              accept="video/*"
              disabled={photos.items.length > 0}
              label={
                photos.items.length > 0 ? 'One video per post, on its own' : 'Video'
              }
            >
              <Clapperboard className="h-5 w-5 text-brand" aria-hidden /> Video
            </PhotoButton>
            <button
              type="button"
              onClick={() => setPickingFeeling((v) => !v)}
              aria-expanded={pickingFeeling}
              className={cn(
                'flex items-center gap-1.5 rounded-pill px-s3 py-1.5 text-sm font-semibold text-ink-2 transition-colors',
                chosen || pickingFeeling ? 'bg-warning-soft' : 'hover:bg-hover',
              )}
            >
              <Smile className="h-5 w-5 text-warning" aria-hidden /> Feeling
            </button>
          </div>
        </div>

        {createPost.isError ? (
          <p className="text-sm font-semibold text-danger">
            {(createPost.error as Error)?.message ?? 'That post did not send.'}
          </p>
        ) : null}

        {/* Only once it starts to matter — a counter at 4,800 remaining is
            noise. */}
        {MAX - body.length <= 500 ? (
          <p
            className={cn(
              'text-right text-sm',
              MAX - body.length < 100 ? 'text-danger' : 'text-ink-2',
            )}
          >
            {MAX - body.length}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

function VisibilityChip({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-1 rounded-sm px-s2 py-0.5 text-sm font-semibold transition-colors',
        active ? 'bg-brand-soft text-brand' : 'bg-surface-2 text-ink-2 hover:bg-hover-2',
      )}
    >
      {icon}
      {label}
    </button>
  )
}
